import type { Edge, Node } from '@xyflow/react'
import { buildGraphFromRecipe } from '@/features/backtest/utils/recipe-builder'
import { materializeNode } from '@/features/backtest/graph-engine/materialize'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { TRADE_MANAGER_TYPE } from '@/features/backtest/schemas/node-schemas/trade-manager-schema'
import { setParameterValue } from '@/features/backtest/components/nodes/universal-node-helpers'
import type { ParametersRecord } from '@/features/backtest/schemas/parameter-value'
import type { ParameterValue } from '@/features/backtest/schemas/parameter-value'
import { applyCollapseState, isProxyEdge } from '@/features/backtest/utils/collapse-model'
import {
  applyDeltaToEdges,
  applyDeltaToNodes,
  planParamChange,
  planWireChanges,
  type Graph,
} from '@/features/backtest/utils/graph-commands'
import { GROUP_TOP_PADDING, IO_PILL_HEIGHT, IO_PILL_X } from '@/features/backtest/utils/subnet-fit'
import type { CookbookEntry, RecipeSocket } from './types'

/**
 * A cookbook recipe used in a strategy (Chris, 2026-10-06; plan
 * docs/superpowers/plans/2026-10-06-cookbook-trade-manager-plan.md).
 *
 * A strategy has one Trade Manager. A recipe never brings its own: it sets
 * the Trade Manager settings it names and arrives as a collapsed subnet
 * whose output feeds one Trade Manager socket (an entry recipe's signal into
 * Trigger, an exit recipe's level into Stop or Target), replacing whatever
 * was wired there. The subnet's input pill reads the strategy's own primary
 * Price Data in place of the recipe's first Price Data step; any further
 * Price Data steps (a second timeframe or symbol) stay inside it as
 * secondary feeds. Every wire goes through the editor's wire commands, so
 * it is checked like a drag. Pure: `makeId` supplies fresh ids; the caller
 * takes one undo snapshot.
 */

export type ApplyRecipeResult =
  | {
      ok: true
      nodes: Node[]
      edges: Edge[]
      /** The subnet the recipe arrived in, when it feeds a socket. */
      subnetId: string | null
      /** Whether it replaced a wire that was already on that socket. */
      replaced: boolean
    }
  | { ok: false; reason: string }

/** The subnet's name for each socket it can feed. */
const SLOT_LABEL: Record<RecipeSocket, string> = {
  trigger: 'Entry',
  stopLevel: 'Stop level',
  targetLevel: 'Target level',
  targetCustom: 'Targets',
}

/** What a template becomes in the strategy, by the socket it feeds (for messages). */
export const TEMPLATE_SLOT_NAME: Record<RecipeSocket, string> = {
  trigger: 'Entry',
  stopLevel: 'stop',
  targetLevel: 'target',
  targetCustom: 'target',
}

const NODE_WIDTH = 280
const NODE_HEIGHT = 240
/** Room for the input pill before the recipe's first node. */
const LEFT_MARGIN = IO_PILL_X + 140
/** Gap between the subnet and the Trade Manager it feeds. */
const GAP = 120
const MIN_SIZE = { width: 640, height: 300 }
/** A Price Data node's width before React Flow has measured it. */
const PRICE_WIDTH = 300

const PRICE_OUTPUT = 'ohlcv'

/** A Trade Manager setting with what it does beyond the value (graph-commands). */
function applyParam(graph: Graph, nodeId: string, key: string, value: unknown): Graph {
  const plan = planParamChange(graph, nodeId, key, value)
  const exposure = new Map(plan.exposure.map((e) => [e.subnetId, e.exposure]))
  const edges = applyDeltaToEdges(graph.edges, plan.delta)
  const nodes = graph.nodes.map((n) => {
    const changed = exposure.get(n.id)
    if (changed) return { ...n, data: { ...n.data, exposure: changed } }
    if (n.id !== nodeId) return n
    return {
      ...n,
      data: {
        ...n.data,
        parameters: setParameterValue(
          n.data?.parameters as ParametersRecord,
          key,
          value as ParameterValue,
        ),
        ...(plan.inputs ? { inputs: plan.inputs } : {}),
      },
    }
  })
  return { nodes: applyDeltaToNodes(nodes, edges, plan.delta), edges }
}

/** A collapsed subnet card's footprint, before React Flow measures it. */
const COLLAPSED = { width: 340, height: 200 }

/** A top-level node's box: measured, else its style size, else a node's usual size. */
function boxOf(node: Node) {
  const style = (node.style ?? {}) as { width?: unknown; height?: unknown }
  const collapsed = node.type === 'subnet' && node.data?.collapsed === true
  const width =
    node.measured?.width ?? (collapsed ? COLLAPSED.width : Number(style.width ?? 0) || NODE_WIDTH)
  const height =
    node.measured?.height ??
    (collapsed ? COLLAPSED.height : Number(style.height ?? 0) || NODE_HEIGHT)
  return { x: node.position.x, y: node.position.y, width, height }
}

/** The first spot at or below `at` where a collapsed card overlaps no top-level node. */
function freeSpot(nodes: Node[], at: { x: number; y: number }): { x: number; y: number } {
  const boxes = nodes.filter((n) => !n.parentId && !n.hidden).map(boxOf)
  const overlaps = (y: number) =>
    boxes.some(
      (b) =>
        at.x < b.x + b.width &&
        b.x < at.x + COLLAPSED.width &&
        y < b.y + b.height &&
        b.y < y + COLLAPSED.height,
    )
  let y = at.y
  for (let tries = 0; tries < 50 && overlaps(y); tries++) y += COLLAPSED.height / 2
  return { x: at.x, y }
}

export function applyRecipe(
  graph: Graph,
  entry: CookbookEntry,
  makeId: (prefix: string) => string,
): ApplyRecipeResult {
  const spec = entry.tradeManager
  if (!spec) return { ok: false, reason: 'This template does not plug into a Trade Manager.' }
  if (entry.unavailable) return { ok: false, reason: entry.unavailable }

  const managers = graph.nodes.filter((n) => n.type === TRADE_MANAGER_TYPE)
  if (managers.length !== 1) {
    return {
      ok: false,
      reason:
        managers.length === 0
          ? 'This strategy has no Trade Manager to plug the template into.'
          : 'This strategy has more than one Trade Manager.',
    }
  }
  const tm = managers[0]
  const feeds = graph.nodes.filter((n) => n.type === 'priceData')
  const primary =
    feeds.find((n) => n.data?.isPrimary === true) ?? (feeds.length === 1 ? feeds[0] : undefined)
  if (!primary) return { ok: false, reason: 'This strategy has no primary Price Data.' }

  // 1. The Trade Manager settings, each with what it does beyond the value
  //    (a Price level stop brings its socket into play first).
  let work: Graph = graph
  for (const [key, value] of Object.entries(spec.params ?? {})) {
    work = applyParam(work, tm.id, key, value)
  }
  if (!spec.feeds) return { ok: true, ...work, subnetId: null, replaced: false }

  // 2. The recipe, built, with fresh ids.
  const built = buildGraphFromRecipe(entry.recipe)
  if (!built.success) return { ok: false, reason: 'This template could not be built.' }
  const builtNodes = built.nodes as unknown as Node[]
  const builtEdges = built.edges as unknown as Edge[]
  const recipePrice =
    builtNodes.find((n) => n.type === 'priceData' && n.data?.isPrimary === true) ??
    builtNodes.find((n) => n.type === 'priceData')
  const prefix = makeId('recipe')
  const idOf = (stepId: string) => `${prefix}-${stepId}`

  const [fromStep, fromHandle] = spec.feeds.from.split('.')
  if (!builtNodes.some((n) => n.id === fromStep) || !fromHandle) {
    return {
      ok: false,
      reason: `The template's output "${spec.feeds.from}" is not one of its steps.`,
    }
  }

  // 3. Layout: the recipe's own left-to-right placement, inside the subnet.
  const kept = builtNodes.filter((n) => n.id !== recipePrice?.id)
  const minX = Math.min(...kept.map((n) => n.position.x))
  const minY = Math.min(...kept.map((n) => n.position.y))
  const maxX = Math.max(...kept.map((n) => n.position.x))
  const maxY = Math.max(...kept.map((n) => n.position.y))
  const size = {
    width: Math.max(MIN_SIZE.width, LEFT_MARGIN + (maxX - minX) + NODE_WIDTH + 160),
    height: Math.max(MIN_SIZE.height, GROUP_TOP_PADDING + 20 + (maxY - minY) + NODE_HEIGHT + 40),
  }
  const pillY = Math.max(GROUP_TOP_PADDING, (size.height - IO_PILL_HEIGHT) / 2)

  const subnetId = makeId('subnet')
  const inId = makeId('groupInput')
  const outId = makeId('groupOutput')
  const socket = spec.feeds.socket
  // Left of the Trade Manager with room to expand, but never over the
  // primary feed: a wide template starts a gap right of it instead. Then down
  // past anything already there (the Entry it replaces stays on the canvas).
  const primaryRight = primary.position.x + (primary.measured?.width ?? PRICE_WIDTH)
  const subnet: Node = {
    id: subnetId,
    type: 'subnet',
    position: freeSpot(work.nodes, {
      x: Math.max(tm.position.x - size.width - GAP, primaryRight + GAP),
      y: tm.position.y + (socket === 'trigger' ? 0 : 320),
    }),
    style: { ...size },
    data: {
      label: entry.name || SLOT_LABEL[socket],
      fontSize: '30px',
      titleEnabled: true,
      color: 'gray',
      collapsed: true,
      expandedSize: { ...size },
      // The grid above cannot know node heights: the editor organizes this
      // subnet the first time its nodes are on screen (utils/subnet-auto-layout.ts).
      autoLayout: true,
    },
  }
  const inPill: Node = {
    id: inId,
    type: 'subnetInput',
    parentId: subnetId,
    expandParent: true,
    position: { x: IO_PILL_X, y: pillY },
    data: { label: 'In 1' },
  }
  const outPill: Node = {
    id: outId,
    type: 'subnetOutput',
    parentId: subnetId,
    expandParent: true,
    position: { x: size.width - 90 - 14, y: pillY },
    data: { label: 'Out' },
  }
  const children: Node[] = kept.map((n) => ({
    ...n,
    id: idOf(n.id),
    parentId: subnetId,
    expandParent: true,
    position: {
      x: LEFT_MARGIN + (n.position.x - minX),
      y: GROUP_TOP_PADDING + 20 + (n.position.y - minY),
    },
    data: {
      ...n.data,
      ...(n.data?.id === n.id ? { id: idOf(n.id) } : {}),
      // A recipe's further price feeds are secondary: the strategy keeps its own primary.
      ...(n.type === 'priceData' ? { isPrimary: false } : {}),
    },
  }))

  // 4. Every wire through the editor's command: the pill fed by the primary
  //    first (a wire out of the pill is checked as what feeds it), then the
  //    recipe's wires with its Price Data read through the pill, then its
  //    output to the pill and the pill to the Trade Manager, replacing what
  //    was on that socket.
  const wire = (
    source: string,
    sourceHandle: string,
    target: string,
    targetHandle: string,
  ): Edge => ({
    id: makeId('edge'),
    source,
    sourceHandle,
    target,
    targetHandle,
    type: 'editable-edge',
  })
  const recipeWires = builtEdges.map((e) =>
    e.source === recipePrice?.id
      ? wire(inId, 'out', idOf(e.target), e.targetHandle ?? '')
      : wire(idOf(e.source), e.sourceHandle ?? '', idOf(e.target), e.targetHandle ?? ''),
  )
  const replaced = work.edges.some(
    (e) => !isProxyEdge(e) && e.target === tm.id && e.targetHandle === socket,
  )
  const withNodes: Graph = {
    nodes: [...work.nodes, subnet, inPill, outPill, ...children],
    edges: work.edges,
  }
  const plan = planWireChanges(
    withNodes,
    {
      add: [
        wire(primary.id, PRICE_OUTPUT, inId, 'in'),
        ...recipeWires,
        wire(idOf(fromStep), fromHandle, outId, 'in'),
        wire(outId, 'out', tm.id, socket),
      ],
    },
    { occupied: 'replace' },
  )
  if (!plan.ok) return { ok: false, reason: plan.reason }
  const edges = applyDeltaToEdges(withNodes.edges, plan.delta)
  const nodes = applyDeltaToNodes(withNodes.nodes, edges, plan.delta)
  return { ok: true, ...applyCollapseState(nodes, edges), subnetId, replaced }
}

/**
 * The run of a new strategy: AAPL on a fixed, recent full session (Chris,
 * 2026-10-06: Apple on a fixed day in September 2026), so the default
 * crossover previews on data that exists and every new strategy opens on the
 * same day. Since 2026-10-28 the Stock Selection owns them (the single run's
 * owner); the primary Price Data carries the same pair as its fallback, which
 * is what a strategy runs on if the Stock Selection is later unwired and what
 * the engine payload goldens pin. A Price Data node added by hand keeps the
 * schema's own default date.
 */
const DEFAULT_RUN = {
  symbol: 'AAPL',
  targetDate: '2026-09-30',
}

/** Primary Price Data settings for a new strategy. */
const DEFAULT_PRICE: Record<string, string | number | boolean> = {
  ...DEFAULT_RUN,
  timeframe: '1m',
  extendedHours: true,
}

/**
 * The default strategy chain (node-first decision 14, the required spine):
 * a Universe into the primary Price Data, a Trade Manager, and the Report it
 * feeds. The Entry is left to the caller (a template applied with
 * `applyRecipe`, or Build entry). `price` overrides the primary feed's
 * settings (a template's timeframe).
 */
export function buildDefaultStrategyGraph(
  price: Record<string, string | number | boolean> = {},
): Graph {
  const make = (
    id: string,
    type: string,
    params: Record<string, string | number | boolean>,
    position: { x: number; y: number },
    isPrimary = false,
  ): Node => {
    const schema = UniversalNodeRegistry.getSchema(type)
    if (!schema) throw new Error(`The "${type}" node schema is not registered`)
    const { node } = materializeNode(
      { id, type, params },
      { schemaById: new Map([[id, schema]]), position, ...(isPrimary ? { isPrimary } : {}) },
    )
    return { ...(node as unknown as Node), data: { ...node.data, nodeId: id } }
  }
  const nodes = [
    make('universe-default', 'universe', DEFAULT_RUN, { x: -300, y: 200 }),
    make(
      'priceData-primary',
      'priceData',
      { ...DEFAULT_PRICE, ...price },
      { x: 100, y: 200 },
      true,
    ),
    make('tradeManager-default', TRADE_MANAGER_TYPE, {}, { x: 1100, y: 160 }),
    make('report-default', 'report', {}, { x: 1500, y: 160 }),
  ]
  const edges: Edge[] = [
    {
      id: 'e-default-universe',
      source: 'universe-default',
      sourceHandle: 'universe',
      target: 'priceData-primary',
      targetHandle: 'universe',
      type: 'editable-edge',
    },
    {
      id: 'e-default-report',
      source: 'tradeManager-default',
      sourceHandle: 'trades',
      target: 'report-default',
      targetHandle: 'trades',
      type: 'editable-edge',
    },
  ]
  return { nodes, edges }
}

/**
 * A template as a whole strategy: the default chain, its primary Price Data
 * on the template's timeframe, and the template applied (Chris, 2026-10-06:
 * a new strategy from a template gets the full default chain). An exit
 * template lands in its slot with the Trigger left for the Entry. Ids are
 * deterministic, so the same template always makes the same graph (the
 * EdgeScript corpus prints from it).
 */
export function templateAsStrategy(entry: CookbookEntry): ApplyRecipeResult {
  const firstFeed = entry.recipe.find((s) => s.type === 'priceData')
  const timeframe = firstFeed?.params?.timeframe
  let n = 0
  const applied = applyRecipe(
    buildDefaultStrategyGraph(typeof timeframe === 'string' ? { timeframe } : {}),
    entry,
    (prefix) => `${prefix}-${entry.key}-${++n}`,
  )
  if (!applied.ok || !applied.subnetId) return applied
  // Left to right with room to expand: the Trade Manager and the Report
  // move right until the template's expanded frame fits before them.
  const subnet = applied.nodes.find((node) => node.id === applied.subnetId)!
  const tm = applied.nodes.find((node) => node.type === TRADE_MANAGER_TYPE)!
  const width = Number((subnet.style as { width?: number } | undefined)?.width ?? 0)
  const shift = Math.max(0, subnet.position.x + width + GAP - tm.position.x)
  if (shift === 0) return applied
  const moved = new Set([tm.id, 'report-default'])
  return {
    ...applied,
    nodes: applied.nodes.map((node) =>
      moved.has(node.id)
        ? { ...node, position: { x: node.position.x + shift, y: node.position.y } }
        : node,
    ),
  }
}
